<template>
  <div class="effects-carousel">
    <div 
    v-if="showButtonLeft"
    class="effects-carousel__button effects-carousel__button-left">
      <ChevronLeft
        @click="currentPage--"
      />
    </div>
    <div class="effects-carousel__item-container">
      <div 
        class="effects-carousel__item"
        :class="{'effects-carousel__item--active': item.name === activeEffect}"
        v-for="item in effects" :key="item.name"
        @click="emits('selectEffect', item)"
      >
        <picture-helper 
          class="effects-carousel__item-image"
          :image-name="`${item.thumbnail}-small`"
          :densities="['1x','2x']"
          :formats="['webp', 'jpg']"
        />
      </div>
    </div>
    <div 
    v-if="showButtonRight"
    class="effects-carousel__button effects-carousel__button-right">
      <ChevronRight
        @click="currentPage++"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { AvailableEffect, EffectName } from '@/static/effectName'
import ChevronRight from '@/assets/ChevronRight.svg'
import ChevronLeft from '@/assets/ChevronLeft.svg'
import PictureHelper from './PictureHelper.vue'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  effects: AvailableEffect[]
  activeEffect: EffectName
}>()

const setInitialCurrentPage = () => {
  currentPage.value = Math.floor(props.effects.findIndex(effect => effect.name === props.activeEffect) / effectsPerPage)
}

watch(() => props.effects, () => {
  setInitialCurrentPage()
}, { immediate: true })

const emits = defineEmits<{
  (event: 'selectEffect', effect: AvailableEffect): void
}>()

const currentPage = ref(0)
const effectsPerPage = 5
const numberOfPages = computed(() => Math.ceil(props.effects.length / effectsPerPage))
const carouselOffset = computed(() => currentPage.value * -251 + 'px')
const showButtonLeft = computed(() => currentPage.value !== 0)
const showButtonRight = computed(() => currentPage.value !== (numberOfPages.value - 1))

</script>
<style lang="scss">
.effects-carousel {
  display: flex;
  width: 100%;
  overflow: hidden;
  position: relative;
  height: 67px;

  &__button {
    width: 50px;
    z-index: 1;
    position: relative;

    > svg {
      fill: $white;
      position: absolute;
      width: 28px;
      height: 22px;
      cursor: pointer;
      padding: 5px 8px 5px 7px;
    }
  }

  &__button-right {
    > svg {
      left: 21px;
      top: 25px;
    }
    background: linear-gradient(to right, rgba($black, 0), rgba($black, 1) 50%);
    margin-left: auto;
  }

  &__button-left {
    > svg {
      right: 21px;
      top: 25px;
    }
    background: linear-gradient(to left, rgba($black, 0), rgba($black, 1) 50%);
  }

  &__item-container {
    height: 67px;
    position: absolute;
    left: v-bind(carouselOffset);
    display: flex;
    gap: 2px;
    padding-left: 20px;
    transition: left 200ms $animationCurve;
  }

  &__item {
    border-radius: 2px;
    min-width: 67px;
    min-height: 67px;
    overflow: hidden;
    position: relative;
    cursor: pointer;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: inherit;
      border: 1px solid transparent;
      transition: border 150ms $animationCurve;
    }

    &:hover::after {
      border: 2px solid $white;
    }

    &-image img {
      width: 100%;
      height: 100%;
    }

    &:hover {
      border-radius: 4px;
    }

    &--active, &--active:hover {
      &::after {
        border: 1px solid $white;
        border-radius: 4px;
      }
    }
  }
}
</style>
